import React, { useRef, useState, useEffect, useCallback } from 'react';
import { Play, Pause, Volume2, VolumeX, Maximize, Minimize, Settings, PictureInPicture, RotateCcw, RotateCw } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { ShareMenu } from './ShareMenu';
import { useVideoAudioFocus } from '../../hooks/useVideoAudioFocus';

interface VideoSource {
 src: string;
 type?: string;
 media?: string;
}

interface CustomVideoPlayerProps {
 src?: string;
 sources?: VideoSource[];
 poster?: string;
 className?: string;
 autoPlay?: boolean;
 // Loop del video. Default OFF: si riproduce una sola volta (niente riavvio a 0).
 loop?: boolean;
 fallbackSrc?: string;
 ariaLabel?: string;
 // L'autoplay parte muto (policy browser); con questa prop il primo gesto
 // utente (hover o click/tap) riattiva l'audio.
 unmuteOnHover?: boolean;
 // Deep-link condivisibile del video (se assente usa l'URL della pagina corrente).
 shareUrl?: string;
 shareTitle?: string;
 shareText?: string;
}

function formatTime(seconds: number): string {
 if (isNaN(seconds)) return"0:00";
 const m = Math.floor(seconds / 60);
 const s = Math.floor(seconds % 60);
 return `${m}:${s.toString().padStart(2, '0')}`;
}

// Utenti con "movimento ridotto": nessuna attivazione audio automatica (evita
// sorprese sonore); restano i controlli manuali.
function prefReduceMotion(): boolean {
 return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export function CustomVideoPlayer({ src, sources, poster, className = '', autoPlay = true, loop = false, fallbackSrc, ariaLabel, unmuteOnHover = false, shareUrl, shareTitle, shareText }: CustomVideoPlayerProps) {
 const { t } = useI18n();
 const containerRef = useRef<HTMLDivElement>(null);
 const videoRef = useRef<HTMLVideoElement>(null);
 
 const [isPlaying, setIsPlaying] = useState(autoPlay);
 // Gli autoplay partono mutati: senza questo il browser li blocca (NotAllowedError).
  const [isMuted, setIsMuted] = useState(autoPlay);
 const [volume, setVolume] = useState(1);

 useEffect(() => {
 if (videoRef.current) videoRef.current.muted = isMuted;
 }, [isMuted]);
 const [progress, setProgress] = useState(0);
 const [currentTime, setCurrentTime] = useState(0);
 const [duration, setDuration] = useState(0);
 const [showControls, setShowControls] = useState(false);
 const [isFullscreen, setIsFullscreen] = useState(false);
 const [playbackRate, setPlaybackRate] = useState(1);
 const [showSettings, setShowSettings] = useState(false);
 const [shouldLoad, setShouldLoad] = useState(false);
  const [pageSettled, setPageSettled] = useState(false);
  const [skipIndicator, setSkipIndicator] = useState<'forward' | 'backward' | null>(null);
  // Poster come <img> eager: resta visibile finche' il video non parte, cosi'
  // l'LCP non dipende dal download del video (montato solo post-load).
  const [showPoster, setShowPoster] = useState(!!poster);

  // Per il doppio tocco su mobile
  const [lastTap, setLastTap] = useState<{ time: number; x: number } | null>(null);

  // Audio focus landing: quando questo video diventa udibile, mette in pausa+muto gli altri
  // (posizione conservata). Copre hero (unmuteOnHover) e coding inline.
  const yieldForOthers = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.pause();
    v.muted = true;
    setIsMuted(true);
  }, []);
  const setFocusRef = useVideoAudioFocus(yieldForOthers);
  // Ref callback STABILE: se inline (identita' nuova a ogni render) React stacca/riattacca il ref
  // a ogni render e il `null` iniziale deregistra il player, azzerando il focus audio (bug takeover).
  const mergedVideoRef = useCallback((el: HTMLVideoElement | null) => {
    videoRef.current = el;
    setFocusRef(el);
  }, [setFocusRef]);

  // Interazione per scorciatoie tastiera + scrub pointer/touch.
  // Pattern adattati da vidstack/player (MIT), muxinc/media-chrome (MIT), sampotts/plyr (MIT).
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [scrubTime, setScrubTime] = useState<number | null>(null);
  const progressBarRef = useRef<HTMLDivElement>(null);
  const scrubbingRef = useRef(false);
  const scrubTimeRef = useRef<number | null>(null);
  const wasPlayingRef = useRef(false);

const controlsTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const safeSetCurrentTime = (value: number) => {
    if (!videoRef.current || !isFinite(value) || value < 0) return;
    videoRef.current.currentTime = value;
  };

 useEffect(() => {
 const video = videoRef.current;
 if (!video) return;

 const handleTimeUpdate = () => {
 setCurrentTime(video.currentTime);
 setProgress((video.currentTime / video.duration) * 100);
 };

 const handleLoadedMetadata = () => {
 setDuration(video.duration);
 };

  const handleEnded = () => {
  setIsPlaying(false);
  setProgress(100);
  };

  const handlePlaying = () => {
  setIsPlaying(true);
  setShowPoster(false);
  };

  video.addEventListener('timeupdate', handleTimeUpdate);
  video.addEventListener('loadedmetadata', handleLoadedMetadata);
  video.addEventListener('ended', handleEnded);
  video.addEventListener('playing', handlePlaying);
  video.addEventListener('play', () => setIsPlaying(true));
  video.addEventListener('pause', () => setIsPlaying(false));

  return () => {
  video.removeEventListener('timeupdate', handleTimeUpdate);
  video.removeEventListener('loadedmetadata', handleLoadedMetadata);
  video.removeEventListener('ended', handleEnded);
  video.removeEventListener('playing', handlePlaying);
  video.removeEventListener('play', () => setIsPlaying(true));
  video.removeEventListener('pause', () => setIsPlaying(false));
  };
  }, [src]);

  useEffect(() => {
  const handleFullscreenChange = () => {
  setIsFullscreen(!!document.fullscreenElement);
  };
  document.addEventListener('fullscreenchange', handleFullscreenChange);
  return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Scorciatoie tastiera, attive quando il player e' "in uso" (hover/focus/fullscreen):
  // quando NON lo e', le frecce restano libere per lo scroll di pagina. Funzionano
  // sia in play sia in pausa. Mappa da vidstack/player (MIT) + plyr (MIT).
  useEffect(() => {
  if (!(isHovered || isFocused || isFullscreen)) return;
  const seekBy = (v: HTMLVideoElement, delta: number) => {
  if (!isFinite(v.duration) || v.duration <= 0) return;
  const t = Math.max(0, Math.min(v.duration, v.currentTime + delta));
  v.currentTime = t;
  setCurrentTime(t);
  setProgress((t / v.duration) * 100);
  };
  const blink = (dir: 'forward' | 'backward') => {
  setSkipIndicator(dir);
  if (skipTimeoutRef.current) clearTimeout(skipTimeoutRef.current);
  skipTimeoutRef.current = setTimeout(() => setSkipIndicator(null), 600);
  };
  const applyVolume = (v: HTMLVideoElement, nv: number) => {
  const clamped = Math.max(0, Math.min(1, nv));
  v.volume = clamped;
  setVolume(clamped);
  if (clamped === 0) { v.muted = true; setIsMuted(true); } else { v.muted = false; setIsMuted(false); }
  };
  const onKey = (e: KeyboardEvent) => {
  const v = videoRef.current;
  if (!v || e.altKey || e.ctrlKey || e.metaKey) return;
  const el = document.activeElement as HTMLElement | null;
  if (el && el.matches('input, textarea, select, [contenteditable="true"]')) return;
  if (e.key === ' ' && el && el.matches('button, [role="menuitem"]')) return;
  const step = e.shiftKey ? 10 : 5;
  if (e.key === ' ' || e.key === 'k' || e.key === 'K') {
  e.preventDefault(); if (v.paused) v.play().catch(() => {}); else v.pause();
  } else if (e.key === 'ArrowRight' || e.key === 'l') {
  e.preventDefault(); seekBy(v, e.key === 'l' ? 10 : step); blink('forward');
  } else if (e.key === 'ArrowLeft' || e.key === 'j') {
  e.preventDefault(); seekBy(v, -(e.key === 'j' ? 10 : step)); blink('backward');
  } else if (e.key === 'ArrowUp') {
  e.preventDefault(); applyVolume(v, Math.round((v.volume + 0.1) * 100) / 100);
  } else if (e.key === 'ArrowDown') {
  e.preventDefault(); applyVolume(v, Math.round((v.volume - 0.1) * 100) / 100);
  } else if (e.key === 'm' || e.key === 'M') {
  e.preventDefault(); v.muted = !v.muted; setIsMuted(v.muted); if (!v.muted && v.volume === 0) applyVolume(v, 1);
  } else if (e.key === 'f' || e.key === 'F') {
  e.preventDefault();
  if (!document.fullscreenElement) containerRef.current?.requestFullscreen().catch(() => {}); else void document.exitFullscreen();
  } else if (/^[0-9]$/.test(e.key)) {
  e.preventDefault();
  if (isFinite(v.duration) && v.duration > 0) { const t = (v.duration / 10) * Number(e.key); v.currentTime = t; setCurrentTime(t); setProgress((t / v.duration) * 100); blink('forward'); }
  }
  };
  window.addEventListener('keydown', onKey);
  return () => window.removeEventListener('keydown', onKey);
  }, [isHovered, isFocused, isFullscreen]);

 useEffect(() => {
 if (!containerRef.current) return;
 const observer = new IntersectionObserver(
 (entries) => {
 if (entries[0]?.isIntersecting) {
 setShouldLoad(true);
 observer.disconnect();
 }
 },
 { rootMargin: '200px' }
 );
observer.observe(containerRef.current);
  return () => observer.disconnect();
}, []);

  const sourcesReady = shouldLoad && pageSettled;

  // Autoplay programmatico: parte solo a sorgenti montate e video bufferizzato.
  useEffect(() => {
  const video = videoRef.current;
  if (!video || !autoPlay || !sourcesReady) return;
  const start = () => video.play().catch((e) => console.warn('CustomVideoPlayer autoplay failed', e));
  if (video.readyState >= 3) start();
  else {
  video.addEventListener('canplay', start, { once: true });
  return () => video.removeEventListener('canplay', start);
  }
  }, [autoPlay, sourcesReady, src]);

  // Le sorgenti video entrano solo a pagina caricata + idle: il poster resta
  // il candidato LCP e il download non contende banda col first paint.
  useEffect(() => {
  let cancelled = false;
  const settle = () => {
  if (cancelled) return;
  if (typeof window.requestIdleCallback === 'function') {
  window.requestIdleCallback(() => { if (!cancelled) setPageSettled(true); }, { timeout: 1500 });
  } else {
  setTimeout(() => { if (!cancelled) setPageSettled(true); }, 500);
  }
  };
  if (document.readyState === 'complete') settle();
  else {
  window.addEventListener('load', settle, { once: true });
  // Rete lenta / load bloccato: non far mai aspettare oltre 3.5s per l'autoplay.
  setTimeout(settle, 3500);
  }
  return () => {
  cancelled = true;
  window.removeEventListener('load', settle);
  };
  }, []);

 const unmute = () => {
 const v = videoRef.current;
 if (!v) return;
 if (v.muted) { v.muted = false; setIsMuted(false); }
 if (v.volume === 0) { v.volume = 1; setVolume(1); }
 };

 const togglePlay = () => {
 if (videoRef.current) {
 if (videoRef.current.paused) videoRef.current.play().catch(e => console.warn('CustomVideoPlayer play() failed', e));
 else videoRef.current.pause();
 }
 };

 const handlePointerDown = (e: React.PointerEvent) => {
 // eslint-disable-next-line react-hooks/purity -- event handler, non render
 const now = Date.now();
 const clientX = e.clientX;
  // Il click/tap e' un gesto utente valido: riattiva l'audio se richiesto.
  if (unmuteOnHover && !prefReduceMotion()) unmute();

 if (lastTap && (now - lastTap.time) < 300) {
 // Doppio tocco rilevato
 const rect = containerRef.current?.getBoundingClientRect();
 if (rect) {
 if (clientX - rect.left < rect.width / 2) {
 // Sinistra -> Indietro 10s
if (videoRef.current) safeSetCurrentTime(Math.max(0, videoRef.current.currentTime - 10));
  showSkip('backward');
 } else {
 // Destra -> Avanti 10s
if (videoRef.current) safeSetCurrentTime(Math.min(duration, videoRef.current.currentTime + 10));
  showSkip('forward');
 }
 }
 setLastTap(null); // Resetta per evitare tripli tocchi fastidiosi
 } else {
  setLastTap({ time: now, x: clientX });
  // Touch: il primo tap mostra i controlli (su mobile non c'e' hover), il secondo
  // fa play/pausa. Mouse: tap -> play/pausa (i controlli compaiono in hover).
  if (e.pointerType === 'touch' && !showControls) {
  setShowControls(true);
  if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
  controlsTimeoutRef.current = setTimeout(() => {
  if (videoRef.current && !videoRef.current.paused && !scrubbingRef.current) setShowControls(false);
  }, 3500);
  } else {
  togglePlay();
  }
  }
  };

 const showSkip = (direction: 'forward' | 'backward') => {
 setSkipIndicator(direction);
 if (skipTimeoutRef.current) clearTimeout(skipTimeoutRef.current);
 skipTimeoutRef.current = setTimeout(() => {
 setSkipIndicator(null);
 }, 600);
 };

 const toggleMute = (e: React.MouseEvent) => {
 e.stopPropagation();
 if (videoRef.current) {
 videoRef.current.muted = !videoRef.current.muted;
 setIsMuted(videoRef.current.muted);
 if (!videoRef.current.muted && volume === 0) {
 handleVolumeChange(1); // Se era muto a zero, ripristina volume al 100%
 }
 }
 };

 const handleVolumeChange = (newVolume: number) => {
 if (videoRef.current) {
 videoRef.current.volume = newVolume;
 setVolume(newVolume);
 if (newVolume === 0) {
 videoRef.current.muted = true;
 setIsMuted(true);
 } else {
 videoRef.current.muted = false;
 setIsMuted(false);
 }
 }
 };

 const toggleFullscreen = (e: React.MouseEvent) => {
 e.stopPropagation();
 if (!containerRef.current) return;
 
 if (!document.fullscreenElement) {
 containerRef.current.requestFullscreen().catch(err => {
 console.error(`Error attempting to enable fullscreen: ${err.message}`);
 });
 } else {
 document.exitFullscreen();
 }
 };

 const togglePiP = async (e: React.MouseEvent) => {
 e.stopPropagation();
 if (!videoRef.current) return;
 try {
 if (document.pictureInPictureElement) {
 await document.exitPictureInPicture();
 } else {
 await videoRef.current.requestPictureInPicture();
 }
 } catch (err) {
 console.error(err);
 }
 };

 const changePlaybackRate = (rate: number, e: React.MouseEvent) => {
 e.stopPropagation();
 if (videoRef.current) {
 videoRef.current.playbackRate = rate;
 setPlaybackRate(rate);
 setShowSettings(false);
 }
 };

  // Scrub con Pointer Events (mouse + touch): preview durante il drag, commit su release.
  // Pattern adattato da vidstack/player (MIT) slider/events-controller.ts + media-chrome (MIT).
  const ratioFromClientX = (clientX: number) => {
    const bar = progressBarRef.current;
    if (!bar) return 0;
    const rect = bar.getBoundingClientRect();
    return Math.max(0, Math.min(1, (clientX - rect.left) / rect.width));
  };

  const beginScrub = (e: React.PointerEvent<HTMLDivElement>) => {
    e.stopPropagation();
    const v = videoRef.current;
    if (!v || !isFinite(v.duration) || v.duration <= 0) return;
    // Pointer capture: il drag continua anche se il dito/mouse esce dalla barra.
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* jsdom/no-op */ }
    scrubbingRef.current = true;
    wasPlayingRef.current = !v.paused;
    const t = ratioFromClientX(e.clientX) * v.duration;
    scrubTimeRef.current = t;
    setScrubTime(t);
  };

  const moveScrub = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrubbingRef.current) return;
    const v = videoRef.current;
    if (!v || !isFinite(v.duration) || v.duration <= 0) return;
    const t = ratioFromClientX(e.clientX) * v.duration;
    scrubTimeRef.current = t;
    setScrubTime(t);
  };

  const endScrub = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!scrubbingRef.current) return;
    scrubbingRef.current = false;
    try { if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* noop */ }
    const v = videoRef.current;
    const t = scrubTimeRef.current;
    if (v && t != null && isFinite(v.duration) && v.duration > 0) {
      safeSetCurrentTime(t);
      setCurrentTime(t);
      setProgress((t / v.duration) * 100);
    }
    if (wasPlayingRef.current && v) v.play().catch(() => {});
    scrubTimeRef.current = null;
    setScrubTime(null);
  };

  const handleMouseMove = () => {
  setShowControls(true);
  if (controlsTimeoutRef.current) clearTimeout(controlsTimeoutRef.current);
  controlsTimeoutRef.current = setTimeout(() => {
  if (isPlaying && !showSettings && !scrubbingRef.current) setShowControls(false);
  }, 2500);
  };

  const displayTime = scrubTime ?? currentTime;
  const shownPct = duration > 0 ? (displayTime / duration) * 100 : progress;

  return (
  <div 
  ref={containerRef}
  className={`relative group overflow-hidden bg-black flex justify-center items-center ${isFullscreen ? 'w-screen h-screen' : className} select-none`}
  onMouseMove={handleMouseMove}
  onMouseEnter={() => { setIsHovered(true); if (unmuteOnHover && !prefReduceMotion()) unmute(); }}
  onMouseLeave={() => {
  setIsHovered(false);
  if (!showSettings) setShowControls(false);
  }}
  onFocus={() => setIsFocused(true)}
  onBlur={() => setIsFocused(false)}
  onPointerDown={handlePointerDown}
  >
  <video
    ref={mergedVideoRef}
    poster={poster}
    muted={isMuted}
    loop={loop}
    playsInline
    aria-label={ariaLabel}
    preload={autoPlay ? 'auto' : 'metadata'}
    // object-cover in card mode: fills the container (both 9:16)
    // object-contain in fullscreen: whole video visible, centered, no cropping
    className={`transition-all duration-700 ease-in-out ${
      isFullscreen
        ? 'max-h-full max-w-full w-auto h-auto object-contain'
        : `w-full h-full object-cover ${className && className.includes('aspect-') ? '' : 'aspect-[9/16]'}`
    }`}
 onError={(e) => {
 // Fallback catastrofico: se ogni <source> fallisce, tenta il fallbackSrc esplicito
 if (fallbackSrc && !e.currentTarget.currentSrc.endsWith(fallbackSrc)) {
 e.currentTarget.src = fallbackSrc;
 e.currentTarget.load();
 if (autoPlay) e.currentTarget.play().catch(err => console.warn('CustomVideoPlayer: Fallback error', err));
 }
 }}
 >
<track kind="captions" />
  {sourcesReady && sources && sources.map((s, idx) => (
  <source key={idx} src={s.src} type={s.type || 'video/mp4'} media={s.media} />
  ))}
  {sourcesReady && src && !sources && <source src={src} type="video/mp4" />}
  </video>

  {poster && showPoster && (
  <img
  src={poster}
  alt=""
  aria-hidden="true"
  fetchPriority="high"
  decoding="async"
  className={`absolute inset-0 pointer-events-none transition-opacity duration-700 ${isFullscreen ? 'max-h-full max-w-full w-auto h-auto object-contain m-auto' : 'w-full h-full object-cover'}`}
  />
  )}

 {/* Center Play/Pause Button */}
 <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 pointer-events-none ${!isPlaying || (showControls && !isPlaying) ? 'opacity-100' : 'opacity-0'}`}>
 <div className="w-20 h-20 rounded-full bg-black/50 backdrop-blur-md flex items-center justify-center text-white border border-white/20 shadow-2xl transition-transform transform scale-100 group-hover:scale-110">
 {isPlaying ? <Pause size={36} /> : <Play size={36} className="ml-1" />}
 </div>
 </div>

 {/* Skip Indicators */}
 {skipIndicator && (
 <div className={`absolute top-1/2 -translate-y-1/2 ${skipIndicator === 'backward' ? 'left-1/4' : 'right-1/4'} pointer-events-none animate-in zoom-in fade-in duration-300`}>
 <div className="w-16 h-16 rounded-full bg-black/40 backdrop-blur-sm flex flex-col items-center justify-center text-white text-xs font-bold gap-1 shadow-2xl">
 {skipIndicator === 'backward' ? <RotateCcw size={24}/> : <RotateCw size={24}/>}
 <span>10s</span>
 </div>
 </div>
 )}

 {/* Share Button (Top Right) — sempre visibile: il video e' un elemento condivisibile */}
 <div className="absolute top-4 right-4 z-10">
 <ShareMenu
 url={shareUrl ?? (typeof window !== 'undefined' ? window.location.href : '')}
 title={shareTitle ?? (typeof document !== 'undefined' ? document.title : 'Jarvis Interviews')}
 text={shareText}
 align="right"
 />
 </div>

 {/* Bottom Controls Bar */}
        <div
          className={`absolute bottom-0 left-0 right-0 p-4 pt-12 bg-gradient-to-t from-black/90 via-black/50 to-transparent transition-opacity duration-300 ${showControls || !isPlaying ? 'opacity-100' : 'opacity-0'}`}
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
        >
 {/* Progress Bar: draggable (pointer/touch), role slider, hit-area h-8 */}
 <div
 ref={progressBarRef}
 role="slider"
 tabIndex={0}
 aria-label={t('video', 'seek') || 'Seek'}
 aria-valuemin={0}
 aria-valuemax={isFinite(duration) ? Math.round(duration) : 0}
 aria-valuenow={isFinite(displayTime) ? Math.round(displayTime) : 0}
 aria-valuetext={`${formatTime(displayTime)} / ${formatTime(duration)}`}
 className="w-full h-8 flex items-center cursor-pointer group/bar relative touch-none select-none"
 onPointerDown={beginScrub}
 onPointerMove={moveScrub}
 onPointerUp={endScrub}
 onPointerCancel={endScrub}
 >
 <div className="w-full h-1.5 group-hover/bar:h-2.5 transition-[height] bg-white/20 rounded-full overflow-hidden relative">
 <div 
 className="absolute top-0 left-0 bottom-0 bg-purple-500 rounded-full"
 style={{ width: `${shownPct}%` }}
 />
 </div>
 {/* Seek Thumb: visibile in hover e durante lo scrub (anche su touch) */}
 <div 
 className={`absolute h-4 w-4 bg-white rounded-full shadow-md transition-transform -ml-2 pointer-events-none ${scrubTime != null ? 'scale-110' : 'scale-0 group-hover/bar:scale-100'}`}
 style={{ left: `${shownPct}%` }}
 />
 {/* Preview tempo durante il drag */}
 {scrubTime != null && (
 <div
 className="absolute -top-8 -translate-x-1/2 px-2 py-0.5 rounded-md bg-black/85 text-white text-xs font-medium tabular-nums pointer-events-none whitespace-nowrap"
 style={{ left: `${shownPct}%` }}
 >
 {formatTime(scrubTime)}
 </div>
 )}
 </div>

 <div className="flex items-center justify-between mt-2">
 {/* Left Controls */}
 <div className="flex items-center gap-4 text-white">
 <button onClick={togglePlay} aria-label={isPlaying ? 'Pause' : 'Play'} className="hover:text-purple-400 transition-colors">
 {isPlaying ? <Pause size={22} /> : <Play size={22} />}
 </button>

<button onClick={toggleMute} aria-label={isMuted || volume === 0 ? 'Unmute' : 'Mute'} className="hover:text-purple-400 transition-colors">
                    {isMuted || volume === 0 ? <VolumeX size={22} /> : <Volume2 size={22} />}
                  </button>
 
 <span className="text-xs font-medium tabular-nums opacity-80 hidden sm:inline">
 {formatTime(displayTime)} / {formatTime(duration)}
 </span>
 </div>
 
 {/* Right Controls */}
 <div className="flex items-center gap-4 text-white relative">
 {/* Settings Menu */}
 {showSettings && (
 <div className="absolute bottom-full right-10 mb-4 bg-black/90 backdrop-blur-md border border-border-subtle rounded-xl p-2 shadow-2xl animate-in fade-in zoom-in-95 min-w-[120px]">
 <div className="text-xs font-bold text-white/50 px-2 py-1 uppercase tracking-wider mb-1">{t('video', 'speed') || 'Velocità'}</div>
 {[0.5, 1, 1.25, 1.5, 2].map(rate => (
 <button
 key={rate}
 onClick={(e) => changePlaybackRate(rate, e)}
 className={`block w-full text-left px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${playbackRate === rate ? 'bg-purple-500/20 text-purple-300' : 'hover:bg-white/10 text-white'}`}
 >
 {rate === 1 ? (t('video', 'normal') || 'Normale') : `${rate}x`}
 </button>
 ))}
 </div>
 )}
 
 <button
 onClick={(e) => { e.stopPropagation(); setShowSettings(!showSettings); }}
 aria-label="Playback settings"
 className={`hover:text-purple-400 transition-colors ${showSettings ? 'text-purple-400 rotate-90' : ''} duration-300`}
 >
 <Settings size={20} />
 </button>

 {/* PiP button (if supported) */}
 {document.pictureInPictureEnabled && (
 <button onClick={togglePiP} aria-label="Picture in picture" className="hover:text-purple-400 transition-colors hidden sm:block">
 <PictureInPicture size={20} />
 </button>
 )}

 <button onClick={toggleFullscreen} aria-label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} className="hover:text-purple-400 transition-colors">
 {isFullscreen ? <Minimize size={20} /> : <Maximize size={20} />}
 </button>
 </div>
 </div>
 </div>
 </div>
 );
}
